import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    SelectChangeEvent,
    Typography,
    Box,
} from '@mui/material';
import { ComboBox, DatePicker, InfoText, PhoneInputField, UCNField, YesNoSelector } from '@fnol/library/src/components';
import {
    constValues,
    damageDescriptionCharacterLimit,
    GenericObject,
    getApplicationId,
    getBirthDateFromUCN,
    getIsSalesForce,
} from '@fnol/library/src/lib';
import {
    personType,
    translations,
    nameMaxLength,
    surnameMaxLength,
    ucnMaxLength,
    question50MaxLength,
    questionMaxLength,
    genderOptions,
    question25MaxLength,
    personalDocumentOptions,
    riskTypeLife,
} from '../../lib/consts';
import '../PolicyDetailScreen/policy.css';

type PersonProps = {
    type: personType;
    id?: number;
};

const Individual = ({ type, id }: PersonProps) => {
    const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<GenericObject>();
    const [open, setOpen] = useState(false);

    const idIndividual = id ? id : '';

    const name = `${personType[type]}${idIndividual}Name`;
    const lastname = `${personType[type]}${idIndividual}LastName`;
    const docType = `${personType[type]}${idIndividual}PersonalDocumentType`;
    const docNumber = `${personType[type]}${idIndividual}PersonalDocumentNumber`;
    const issuePlace = `${personType[type]}${idIndividual}DocumentIssuePlace`;
    const issueDate = `${personType[type]}${idIndividual}DocumentIssueDate`;
    const placeOfBirth = `${personType[type]}${idIndividual}PlaceOfBirth`;
    const gender = `${personType[type]}${idIndividual}Gender`;
    const dateOfBirth = `${personType[type]}${idIndividual}DateOfBirth`;
    const ucn = `${personType[type]}${idIndividual}UCN`;
    const citizenship = `${personType[type]}${idIndividual}Citizenship`;
    const dualCitizenship = `${personType[type]}${idIndividual}DualCitizenship`;
    const country = `${personType[type]}${idIndividual}Country`;
    const countryText = `${personType[type]}${idIndividual}MyCountry`;
    const street = `${personType[type]}${idIndividual}Street`;
    const municipality = `${personType[type]}${idIndividual}Municipality`;
    const city = `${personType[type]}${idIndividual}City`;
    const cityInSerbia = `${personType[type]}${idIndividual}CityInSerbia`;
    const zipCode = `${personType[type]}${idIndividual}ZipCode`;
    const phone = `${personType[type]}${idIndividual}Phone`;
    const email = `${personType[type]}${idIndividual}Email`;
    const duplicatedUcn = `${personType[type]}${idIndividual}DuplicatedUcn`;

    const currentResidenceAddress = `${personType[type]}${idIndividual}CurrentResidenceAddress`;
    const currentResidenceStreet = `${personType[type]}${idIndividual}CurrentResidenceStreet`;
    const currentResidenceMunicipality = `${personType[type]}${idIndividual}CurrentResidenceMunicipality`;
    const currentResidenceCity = `${personType[type]}${idIndividual}CurrentResidenceCity`;
    const currentResidenceCityInSerbia = `${personType[type]}${idIndividual}CurrentResidenceCityInSerbia`;
    const currentResidenceZipCode = `${personType[type]}${idIndividual}CurrentResidenceZipCode`;

    const mailingAddress = `${personType[type]}${idIndividual}MailingAddress`;
    const mailingStreet = `${personType[type]}${idIndividual}MailingStreet`;
    const mailingMunicipality = `${personType[type]}${idIndividual}MailingMunicipality`;
    const mailingCity = `${personType[type]}${idIndividual}MailingCity`;
    const mailingCityInSerbia = `${personType[type]}${idIndividual}MailingCityInSerbia`;
    const mailingZipCode = `${personType[type]}${idIndividual}MailingZipCode`;

    const statement = `${personType[type]}${idIndividual}Statement`;
    const statementNote = `${personType[type]}${idIndividual}StatementNote`;

    const ucnOnBlur = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        checkIfUCNExists(e);
        const birthDate = getBirthDateFromUCN(e.target?.value);
        if (!!birthDate) setFieldValue(dateOfBirth, birthDate);
    };

    const checkIfUCNExists = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const insertTypes: number[] = [1, 2, 3];
        const userIds = [1, 2, 3, 4, 5];
        const insertedUcns: GenericObject = {};

        insertTypes.map((insertType) => {
            if (insertType === personType.user) {
                userIds.map((userId) => {
                    insertedUcns[`${personType[insertType]}${userId}UCN`] =
                        values[`${personType[insertType]}${userId}UCN`];
                });
            } else {
                insertedUcns[`${personType[insertType]}UCN`] = values[`${personType[insertType]}UCN`];
            }
        });

        let duplicatedUcnExists = false;

        for (const [key, value] of Object.entries(insertedUcns)) {
            if (e.target?.value && e.target?.value !== '') {
                if (type === personType.user) {
                    if (value === e.target?.value) {
                        duplicatedUcnExists = true;
                    }
                } else {
                    if (value === e.target?.value && key !== `${personType[type]}${idIndividual}UCN`) {
                        duplicatedUcnExists = true;
                    }
                }
            }
        }

        setFieldValue(duplicatedUcn, duplicatedUcnExists);
    };

    const zipCodeValue = (cityValue: string) => {
        const filteredCity = values.cities?.find((item: GenericObject) => {
            if (values[country] === JSON.stringify(constValues.countrySerbiaId) && values[cityValue]) {
                return item.label === values[cityValue]?.label;
            }
        });
        return filteredCity ? filteredCity.zipCode : '';
    };

    useEffect(() => {
        if (values[country] === JSON.stringify(constValues.countrySerbiaId)) {
            if (!!zipCodeValue(cityInSerbia) && values[cityInSerbia]) {
                setFieldValue(zipCode, zipCodeValue(cityInSerbia));
            }
            if (!!zipCodeValue(currentResidenceCityInSerbia) && values[currentResidenceCityInSerbia]) {
                setFieldValue(currentResidenceZipCode, zipCodeValue(currentResidenceCityInSerbia));
            }
            if (!!zipCodeValue(mailingCityInSerbia) && values[mailingCityInSerbia]) {
                setFieldValue(mailingZipCode, zipCodeValue(mailingCityInSerbia));
            }
        }

        if (values[country] !== constValues.countrySerbiaId && !values[city]) {
            setFieldValue(zipCode, '');
            setFieldValue(currentResidenceZipCode, '');
            setFieldValue(mailingZipCode, '');
        }
    }, [values[cityInSerbia], values[currentResidenceCityInSerbia], values[mailingCityInSerbia], values[city]]);

    // selected country textual representation
    const selectedCountry = values.preparedCountries?.find(
        (item: GenericObject) => item.id === parseInt(values[country] as string),
    );
    values[countryText] = selectedCountry?.text;

    const handleChangeCountry = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(country, event.target.value);
        if (!values.risks.includes(riskTypeLife.outlive)) {
            setFieldValue(street, '');
            setFieldValue(city, '');
            setFieldValue(cityInSerbia, '');
            setFieldValue(ucn, '');
            setFieldValue(zipCode, '');
            setFieldValue(currentResidenceCity, '');
            setFieldValue(currentResidenceCityInSerbia, '');
            setFieldValue(currentResidenceZipCode, '');
            setFieldValue(mailingCity, '');
            setFieldValue(mailingCityInSerbia, '');
            setFieldValue(mailingZipCode, '');
            setFieldValue(duplicatedUcn, false);
        }
    };

    const handleChangeCurrentResidenceAddress = () => {
        setFieldValue(currentResidenceStreet, '');
        setFieldValue(currentResidenceMunicipality, '');
        setFieldValue(currentResidenceCity, '');
        setFieldValue(currentResidenceCityInSerbia, '');
        setFieldValue(currentResidenceZipCode, '');
    };

    const handleChangeMailingAddress = () => {
        setFieldValue(mailingStreet, '');
        setFieldValue(mailingMunicipality, '');
        setFieldValue(mailingCity, '');
        setFieldValue(mailingCityInSerbia, '');
        setFieldValue(mailingZipCode, '');
    };

    const handleChangeCitizenship = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(citizenship, event.target.value);
    };

    const clearDualCitizenship = () => {
        setFieldValue(dualCitizenship, '');
    };

    return (
        <>
            {type !== personType.submitter && (
                <>
                    <FormControl>
                        <Field
                            name={name}
                            label={translations.personalInfo.NAME}
                            as={TextField}
                            inputProps={{
                                maxLength: nameMaxLength,
                            }}
                            error={!!(touched[name] && errors[name])}
                            required
                        />
                        {touched[name] && errors[name] ? (
                            <FormHelperText error={true}>
                                <>{errors[name]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>

                    <FormControl>
                        <Field
                            name={lastname}
                            label={translations.personalInfo.LAST_NAME}
                            as={TextField}
                            inputProps={{
                                maxLength: surnameMaxLength,
                            }}
                            error={!!(touched[lastname] && errors[lastname])}
                            required
                        />
                        {touched[lastname] && errors[lastname] ? (
                            <FormHelperText error={true}>
                                <>{errors[lastname]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                </>
            )}

            <FormControl required={true}>
                <InputLabel id="gender-type-label">{translations.GENDER}</InputLabel>
                <Select
                    id={gender}
                    labelId="gender-type-label"
                    name={gender}
                    value={values[gender] || ''}
                    label={translations.GENDER}
                    error={!!(touched[gender] && errors[gender])}
                    onChange={handleChange}
                >
                    {genderOptions.map((option) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.value}
                            </MenuItem>
                        );
                    })}
                </Select>

                {touched[gender] && errors[gender] ? (
                    <FormHelperText error={true}>
                        <>{errors[gender]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl error={!!(touched[citizenship] && errors[citizenship])} required>
                <InputLabel id={citizenship + '-label'}>{translations.CITIZENSHIP}</InputLabel>
                <Select
                    id={citizenship}
                    labelId={citizenship + '-label'}
                    name={citizenship}
                    value={values[citizenship] || ''}
                    label={translations.CITIZENSHIP}
                    onChange={handleChangeCitizenship}
                >
                    {values.citizenships?.map((option: GenericObject) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            <FormControl error={!!(touched[country] && errors[country])} required>
                <InputLabel id={country + '-label'}>{translations.COUNTRY}</InputLabel>
                <Select
                    id={country}
                    labelId={country + '-label'}
                    name={country}
                    value={values[country] || ''}
                    label={translations.COUNTRY}
                    onChange={handleChangeCountry}
                >
                    {values.countries?.map((option: GenericObject) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            <FormControl>
                <InputLabel id={dualCitizenship + '-label'}>{translations.personalInfo.DUAL_CITIZENSHIP}</InputLabel>
                <Select
                    id={dualCitizenship}
                    labelId={dualCitizenship + '-label'}
                    name={dualCitizenship}
                    value={values[dualCitizenship] || ''}
                    label={translations.personalInfo.DUAL_CITIZENSHIP}
                    onChange={handleChange}
                >
                    {values.citizenships?.map((option: GenericObject) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                </Select>
                {values[dualCitizenship] !== '' && (
                    <div
                        onClick={() => clearDualCitizenship()}
                        style={{
                            position: 'absolute',
                            right: '40px',
                            top: '15px',
                            cursor: 'pointer',
                        }}
                    >
                        x
                    </div>
                )}
            </FormControl>

            {((values[country] === constValues.countrySerbiaId && !values.risks.includes(riskTypeLife.outlive)) ||
                values.risks.includes(riskTypeLife.outlive)) && (
                <FormControl>
                    <Field
                        name={ucn}
                        label={translations.personalInfo.UCN}
                        as={UCNField}
                        inputProps={{
                            maxLength: ucnMaxLength,
                        }}
                        onBlur={ucnOnBlur}
                        error={!!(touched[ucn] && errors[ucn])}
                        disabled={type === personType.user && values.risks.includes(riskTypeLife.outlive)}
                        required
                    />
                    {touched[ucn] && errors[ucn] ? (
                        <FormHelperText error={true}>
                            <>{errors[ucn]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            {values[duplicatedUcn] && (
                <InfoText
                    label={translations.validationMessages.ALREADY_HAS_UCN}
                    variant="body2"
                    paddingTop={0.5}
                ></InfoText>
            )}

            <FormControl error={!!(touched[dateOfBirth] && errors[dateOfBirth])} className="policyDatePicker">
                <Field
                    name={dateOfBirth}
                    label={translations.DATE_OF_BIRTH}
                    as={DatePicker}
                    disableFuture
                    error={!!(touched[dateOfBirth] && errors[dateOfBirth])}
                    disabled={type === personType.user && values.risks.includes(riskTypeLife.outlive)}
                />
                {touched[dateOfBirth] && errors[dateOfBirth] ? (
                    <FormHelperText error={true}>
                        <>{errors[dateOfBirth]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={placeOfBirth}
                    label={translations.PLACE_OF_BIRTH}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[placeOfBirth] && errors[placeOfBirth])}
                    required
                />
                {touched[placeOfBirth] && errors[placeOfBirth] ? (
                    <FormHelperText error={true}>
                        <>{errors[placeOfBirth]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <Typography variant="subtitle1">{translations.personalInfo.PERMANENT_RESIDENCE}</Typography>

            <FormControl>
                <Field
                    name={street}
                    label={translations.STREET}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[street] && errors[street])}
                    required
                />
                {touched[street] && errors[street] ? (
                    <FormHelperText error={true}>
                        <>{errors[street]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={municipality}
                    label={translations.MUNICIPALITY}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[municipality] && errors[municipality])}
                    required
                />
                {touched[municipality] && errors[municipality] ? (
                    <FormHelperText error={true}>
                        <>{errors[municipality]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {values[country] !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId ? (
                <FormControl>
                    <Field
                        name={city}
                        label={translations.CITY}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[city] && errors[city])}
                        required
                    />
                    {touched[city] && errors[city] ? (
                        <FormHelperText error={true}>
                            <>{errors[city]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : (
                <FormControl>
                    <Field
                        name={cityInSerbia}
                        label={translations.CITY}
                        options={
                            values.cities || [
                                {
                                    id: '',
                                    value: '',
                                    text: '',
                                    code: '',
                                    zipCode: '',
                                },
                            ]
                        }
                        onChange={handleChange}
                        defaultValue={values[cityInSerbia]}
                        as={ComboBox}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[cityInSerbia] && errors[cityInSerbia])}
                        required
                    />
                    {touched[cityInSerbia] && errors[cityInSerbia] ? (
                        <FormHelperText error={true}>
                            <>{errors[cityInSerbia]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            <FormControl>
                <Field
                    name={zipCode}
                    label={translations.ZIP_CODE}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[zipCode] && errors[zipCode])}
                    required
                />
                {touched[zipCode] && errors[zipCode] ? (
                    <FormHelperText error={true}>
                        <>{errors[zipCode]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <Typography variant="subtitle1">{translations.CURRENT_RESIDENCE_ADDRESS}</Typography>
            <FormControl>
                <YesNoSelector name={currentResidenceAddress} onChange={handleChangeCurrentResidenceAddress} />
            </FormControl>

            {values[currentResidenceAddress] === 'false' && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: '15px',
                        paddingLeft: '20px',
                        paddingBottom: '20px',
                    }}
                >
                    <FormControl>
                        <Field
                            name={currentResidenceStreet}
                            label={translations.STREET}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            error={!!(touched[currentResidenceStreet] && errors[currentResidenceStreet])}
                            required
                        />
                        {touched[currentResidenceStreet] && errors[currentResidenceStreet] ? (
                            <FormHelperText error={true}>
                                <>{errors[currentResidenceStreet]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>

                    <FormControl>
                        <Field
                            name={currentResidenceMunicipality}
                            label={translations.MUNICIPALITY}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            error={!!(touched[currentResidenceMunicipality] && errors[currentResidenceMunicipality])}
                            required
                        />
                        {touched[currentResidenceMunicipality] && errors[currentResidenceMunicipality] ? (
                            <FormHelperText error={true}>
                                <>{errors[currentResidenceMunicipality]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>

                    {values[country] !== constValues.countrySerbiaId.toString(10) &&
                    values[country] !== constValues.countrySerbiaId ? (
                        <FormControl>
                            <Field
                                name={currentResidenceCity}
                                label={translations.CITY}
                                as={TextField}
                                inputProps={{
                                    maxLength: question50MaxLength,
                                }}
                                error={!!(touched[currentResidenceCity] && errors[currentResidenceCity])}
                                required
                            />
                            {touched[currentResidenceCity] && errors[currentResidenceCity] ? (
                                <FormHelperText error={true}>
                                    <>{errors[currentResidenceCity]}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    ) : (
                        <FormControl>
                            <Field
                                name={currentResidenceCityInSerbia}
                                label={translations.CITY}
                                options={
                                    values.cities || [
                                        {
                                            id: '',
                                            value: '',
                                            text: '',
                                            code: '',
                                            zipCode: '',
                                        },
                                    ]
                                }
                                onChange={handleChange}
                                defaultValue={values[currentResidenceCityInSerbia]}
                                as={ComboBox}
                                inputProps={{
                                    maxLength: question50MaxLength,
                                }}
                                error={
                                    !!(touched[currentResidenceCityInSerbia] && errors[currentResidenceCityInSerbia])
                                }
                                required
                            />
                            {touched[currentResidenceCityInSerbia] && errors[currentResidenceCityInSerbia] ? (
                                <FormHelperText error={true}>
                                    <>{errors[currentResidenceCityInSerbia]}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    )}

                    <FormControl>
                        <Field
                            name={currentResidenceZipCode}
                            label={translations.ZIP_CODE}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            error={!!(touched[currentResidenceZipCode] && errors[currentResidenceZipCode])}
                            required
                        />
                        {touched[currentResidenceZipCode] && errors[currentResidenceZipCode] ? (
                            <FormHelperText error={true}>
                                <>{errors[currentResidenceZipCode]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                </Box>
            )}

            <Typography variant="subtitle1">{translations.MAILING_ADDRESS}</Typography>
            <FormControl>
                <YesNoSelector name={mailingAddress} onChange={handleChangeMailingAddress} />
            </FormControl>

            {values[mailingAddress] === 'false' && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: '15px',
                        paddingLeft: '20px',
                        paddingBottom: '20px',
                    }}
                >
                    <FormControl>
                        <Field
                            name={mailingStreet}
                            label={translations.STREET}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            error={!!(touched[mailingStreet] && errors[mailingStreet])}
                            required
                        />
                        {touched[mailingStreet] && errors[mailingStreet] ? (
                            <FormHelperText error={true}>
                                <>{errors[mailingStreet]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>

                    <FormControl>
                        <Field
                            name={mailingMunicipality}
                            label={translations.MUNICIPALITY}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            error={!!(touched[mailingMunicipality] && errors[mailingMunicipality])}
                            required
                        />
                        {touched[mailingMunicipality] && errors[mailingMunicipality] ? (
                            <FormHelperText error={true}>
                                <>{errors[mailingMunicipality]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>

                    {values[country] !== constValues.countrySerbiaId.toString(10) &&
                    values[country] !== constValues.countrySerbiaId ? (
                        <FormControl>
                            <Field
                                name={mailingCity}
                                label={translations.CITY}
                                as={TextField}
                                inputProps={{
                                    maxLength: question50MaxLength,
                                }}
                                error={!!(touched[mailingCity] && errors[mailingCity])}
                                required
                            />
                            {touched[mailingCity] && errors[mailingCity] ? (
                                <FormHelperText error={true}>
                                    <>{errors[mailingCity]}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    ) : (
                        <FormControl>
                            <Field
                                name={mailingCityInSerbia}
                                label={translations.CITY}
                                options={
                                    values.cities || [
                                        {
                                            id: '',
                                            value: '',
                                            text: '',
                                            code: '',
                                            zipCode: '',
                                        },
                                    ]
                                }
                                onChange={handleChange}
                                defaultValue={values[mailingCityInSerbia]}
                                as={ComboBox}
                                inputProps={{
                                    maxLength: question50MaxLength,
                                }}
                                error={!!(touched[mailingCityInSerbia] && errors[mailingCityInSerbia])}
                                required
                            />
                            {touched[mailingCityInSerbia] && errors[mailingCityInSerbia] ? (
                                <FormHelperText error={true}>
                                    <>{errors[mailingCityInSerbia]}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    )}

                    <FormControl>
                        <Field
                            name={mailingZipCode}
                            label={translations.ZIP_CODE}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            error={!!(touched[mailingZipCode] && errors[mailingZipCode])}
                            required
                        />
                        {touched[mailingZipCode] && errors[mailingZipCode] ? (
                            <FormHelperText error={true}>
                                <>{errors[mailingZipCode]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                </Box>
            )}

            <FormControl>
                <PhoneInputField
                    name={phone}
                    specialLabel={translations.personalInfo.MOBILE_STAR}
                    country={'rs'}
                    required
                />
            </FormControl>

            <FormControl>
                <Field
                    name={email}
                    label={translations.personalInfo.EMAIL}
                    type="email"
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[email] && errors[email])}
                    required
                />
                {touched[email] && errors[email] ? (
                    <FormHelperText error={true}>
                        <>{errors[email]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl required={true}>
                <InputLabel id="personal-document-type-label">{translations.SELECT_PERSONAL_DOC_TYPE}</InputLabel>
                <Select
                    id={docType}
                    labelId="personal-document-type-label"
                    name={docType}
                    value={values[docType] || ''}
                    label={translations.SELECT_PERSONAL_DOC_TYPE}
                    error={!!(touched[docType] && errors[docType])}
                    onChange={handleChange}
                >
                    {personalDocumentOptions.map((option) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.value}
                            </MenuItem>
                        );
                    })}
                </Select>

                {touched[docType] && errors[docType] ? (
                    <FormHelperText error={true}>
                        <>{errors[docType]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={docNumber}
                    label={translations.PERSONAL_DOC_NUMBER}
                    as={TextField}
                    inputProps={{
                        maxLength: question25MaxLength,
                    }}
                    error={!!(touched[docNumber] && errors[docNumber])}
                    required
                />
                {touched[docNumber] && errors[docNumber] ? (
                    <FormHelperText error={true}>
                        <>{errors[docNumber]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={issuePlace}
                    label={translations.ISSUE_PLACE}
                    as={TextField}
                    inputProps={{
                        maxLength: question25MaxLength,
                    }}
                    error={!!(touched[issuePlace] && errors[issuePlace])}
                    required
                />
                {touched[issuePlace] && errors[issuePlace] ? (
                    <FormHelperText error={true}>
                        <>{errors[issuePlace]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl error={!!(touched[issueDate] && errors[issueDate])} className="policyDatePicker">
                <Field
                    name={issueDate}
                    label={translations.ISSUE_DATE}
                    as={DatePicker}
                    disableFuture
                    error={!!(touched[issueDate] && errors[issueDate])}
                />
                {touched[issueDate] && errors[issueDate] ? (
                    <FormHelperText error={true}>
                        <>{errors[issueDate]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <Typography variant="subtitle1">{translations.policyUser.SUBMITTER_STATEMENT}</Typography>
            <FormControl>
                <YesNoSelector name={statement} />
            </FormControl>

            {values[statement] === 'false' && (
                <FormControl>
                    <Field
                        name={statementNote}
                        label={translations.policyUser.SUBMITTER_STATEMENT_NOTE}
                        as={TextField}
                        inputProps={{
                            maxLength: damageDescriptionCharacterLimit,
                        }}
                        error={!!(touched[statementNote] && errors[statementNote])}
                        required
                    />
                    {touched[statementNote] && errors[statementNote] ? (
                        <FormHelperText error={true}>
                            <>{errors[statementNote]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}
        </>
    );
};

export const IndividualCleanup = (
    type: personType,
    setFieldValue: (field: string, value: string | number, shouldValidate?: boolean | undefined) => void,
    userId?: number,
) => {
    const idUser = userId ? userId : '';
    type !== personType.submitter && setFieldValue(`${personType[type]}${idUser}Name`, '');
    type !== personType.submitter && setFieldValue(`${personType[type]}${idUser}LastName`, '');
    setFieldValue(`${personType[type]}${idUser}DateOfBirth`, '');
    setFieldValue(`${personType[type]}${idUser}UCN`, '');
    setFieldValue(`${personType[type]}${idUser}Citizenship`, '');
    setFieldValue(`${personType[type]}${idUser}DualCitizenship`, '');
    setFieldValue(`${personType[type]}${idUser}Municipality`, '');
    setFieldValue(`${personType[type]}${idUser}ZipCode`, '');
    setFieldValue(`${personType[type]}${idUser}Email`, '');
    setFieldValue(`${personType[type]}${idUser}Phone`, constValues.dialCodeSerbia);
    setFieldValue(`${personType[type]}${idUser}Country`, constValues.countrySerbiaId);
    setFieldValue(`${personType[type]}${idUser}MyCountry`, '');
    setFieldValue(`${personType[type]}${idUser}Street`, '');
    setFieldValue(`${personType[type]}${idUser}City`, '');
    setFieldValue(`${personType[type]}${idUser}CityInSerbia`, '');

    setFieldValue(`${personType[type]}${idUser}CurrentResidenceAddress`, '');
    setFieldValue(`${personType[type]}${idUser}CurrentResidenceStreet`, '');
    setFieldValue(`${personType[type]}${idUser}CurrentResidenceMunicipality`, '');
    setFieldValue(`${personType[type]}${idUser}CurrentResidenceCity`, '');
    setFieldValue(`${personType[type]}${idUser}CurrentResidenceCityInSerbia`, '');
    setFieldValue(`${personType[type]}${idUser}CurrentResidenceZipCode`, '');

    setFieldValue(`${personType[type]}${idUser}MailingAddress`, '');
    setFieldValue(`${personType[type]}${idUser}MailingStreet`, '');
    setFieldValue(`${personType[type]}${idUser}MailingMunicipality`, '');
    setFieldValue(`${personType[type]}${idUser}MailingCity`, '');
    setFieldValue(`${personType[type]}${idUser}MailingCityInSerbia`, '');
    setFieldValue(`${personType[type]}${idUser}MailingZipCode`, '');

    setFieldValue(`${personType[type]}${idUser}PersonalDocumentType`, '');
    setFieldValue(`${personType[type]}${idUser}PersonalDocumentNumber`, '');
    setFieldValue(`${personType[type]}${idUser}DocumentIssuePlace`, '');
    setFieldValue(`${personType[type]}${idUser}DocumentIssueDate`, '');
    setFieldValue(`${personType[type]}${idUser}PlaceOfBirth`, '');
    setFieldValue(`${personType[type]}${idUser}Gender`, '');
    setFieldValue(`${personType[type]}${idUser}Statement`, '');
    setFieldValue(`${personType[type]}${idUser}StatementNote`, '');

    getIsSalesForce(getApplicationId()) && setFieldValue(`${personType[type]}${idUser}AttorneySignature`, '');
};

export default Individual;
